import { DurationConverter, RandomUtils } from '@colonist/utils';
import { FAQ_URL } from '@colonist/consts';
import { throttle } from 'lodash-es';
import { UIUtilsEjsVariables } from '../UIUtilsEjsVariables.ts';
import { clientIntl } from '../ClientI18n.ts';
import { UIPopupManager } from '../ui_popup/UIPopupManager.ts';
// https://stackoverflow.com/a/901144/4204919
export function getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
// Apr 17, 2022 Omar: There is a bug in WebKit Safari 15.4 where it uses WebGL via Metal by default and breaks anti-aliasing.
// This tries to detect if the client is running a version of Safari that is affected by this bug, to disable anti-aliasing.
//
// Bug reference: https://bugs.webkit.org/show_bug.cgi?id=237906
//
export function isAffectedBySafariWebGlBug() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isSafari = userAgent.indexOf('safari') > -1 && userAgent.indexOf('chrome') == -1;
    const isAffectedVersion = userAgent.indexOf('version/15.4') >= 0;
    const isAffectedDevice = userAgent.match(/cpu (os|iphone os) 15_4 like mac os x/) !== null;
    return isSafari && (isAffectedVersion || isAffectedDevice);
}
// Apr 30, 2026: Firefox 149+ on Windows has a hardware-accelerated WebGL regression that causes
// progressive performance degradation until the WebGL context is lost.
// Disabling anti-aliasing reduces GPU pressure and delays the crash.
//
// User workaround: in about:config set webgl.forbid-hardware=true, webgl.forbid-software=false
//
export function isAffectedByFirefoxWebGlBug() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const firefoxVersionMatch = userAgent.match(/firefox\/(\d+)/);
    const firefoxVersion = firefoxVersionMatch ? parseInt(firefoxVersionMatch[1], 10) : 0;
    const isAffectedFirefox = firefoxVersion >= 149;
    const isWindows = userAgent.indexOf('windows') > -1;
    return isAffectedFirefox && isWindows;
}
export function isMobileLandscapeView() {
    const breakpointWidthSmall = 480;
    const breakpointWidthLarge = 991;
    if (window.innerWidth < breakpointWidthSmall) return false;
    if (window.innerWidth > breakpointWidthLarge) return false;
    return true;
}
export function sendToAppropriateMobileStore() {
    const random = RandomUtils.randomBool();
    const urlGooglePlay = 'https://play.google.com/store/apps/details?id=com.colonist.colonist';
    const urlAppStore = 'https://apps.apple.com/us/app/colonist-io/id6444831129';
    if (random) {
        navigateToPage(urlGooglePlay, true);
        return;
    }
    navigateToPage(urlAppStore, true);
}
export function throttleUserAction(functionToThrottle) {
    return throttle(functionToThrottle, DurationConverter.secondsAsMilliseconds(2), {
        trailing: false
    });
}
let inGloballyExclusiveHandler = false;
export function createGloballyExclusiveHandler(handler) {
    return async (event)=>{
        if (inGloballyExclusiveHandler) {
            return;
        }
        inGloballyExclusiveHandler = true;
        try {
            await handler(event);
        } finally{
            inGloballyExclusiveHandler = false;
        }
    };
}
export function getHomepageUrl() {
    const languagePrefix = clientIntl.getLanguagePathPrefix();
    if (UIUtilsEjsVariables.isUserOnDiscordMobile) return `/.proxy${languagePrefix}/discord/mobile`;
    if (UIUtilsEjsVariables.isUserOnDiscord) return `/.proxy${languagePrefix}/discord/web`;
    if (UIUtilsEjsVariables.isUserOnMobile || UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return `${languagePrefix}/mobile`;
    return `${languagePrefix}/`;
}
export function getHomepageUrlWithParameter(parameter, parameterValue) {
    return `${getHomepageUrl()}#${parameter}=${parameterValue}`;
}
export function getMobileRulebookUrl() {
    const languagePrefix = clientIntl.getLanguagePathPrefix();
    if (UIUtilsEjsVariables.isUserOnDiscordMobile) return `/.proxy${languagePrefix}/catan-rules?style=mobile`;
    return `${languagePrefix}/catan-rules?style=mobile`;
}
export function navigateToPage(path, shouldOpenNewTab = false, shouldOpenInAppWithoutReload = false) {
    UIPopupManager.closeAllPopups();
    const isOnMobileAppOrWeb = UIUtilsEjsVariables.isUserOnMobileAppOrWeb;
    const isOnDiscordMobile = UIUtilsEjsVariables.isUserOnDiscordMobile;
    if (UIUtilsEjsVariables.isUserOnDiscord && isInternalFaqPath(path)) return openUrlInMobileExternalBrowser(getExternalFaqUrl(path));
    const isExternalUrl = !path.startsWith('/') && !path.includes(window.location.origin);
    const isAuthUrl = path.includes('/auth/') || path.includes('/auth-link/');
    const isInternalMobilePage = isOnMobileAppOrWeb && !isExternalUrl && !isAuthUrl;
    const isDiscordMobileInAppNav = isOnDiscordMobile && !isExternalUrl && !isAuthUrl && !shouldOpenNewTab;
    if ((isInternalMobilePage || isDiscordMobileInAppNav) && shouldOpenInAppWithoutReload) return navigateToPageOnMobileAppViaLink(path);
    if (isDiscordMobileInAppNav) {
        const fullUrl = path.startsWith('/') ? `${window.location.origin}${path}` : path;
        // eslint-disable-next-line no-restricted-syntax
        window.location.href = fullUrl;
        return;
    }
    if (isInternalMobilePage) return navigateToPageOnMobileAppViaReload(path);
    const url = path.startsWith('#') ? `${window.location.origin}/${path}` : path;
    if ((isOnMobileAppOrWeb || isOnDiscordMobile) && shouldOpenNewTab) return openUrlInMobileExternalBrowser(url);
    // eslint-disable-next-line no-restricted-syntax
    window.open(url, shouldOpenNewTab ? '_blank' : '_self');
}
function openUrlInMobileExternalBrowser(url) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
}
function isInternalFaqPath(path) {
    if (path == '/faq') return true;
    if (path.startsWith('/faq#')) return true;
    if (path.startsWith('/faq?')) return true;
    return false;
}
function getExternalFaqUrl(path) {
    const hashIndex = path.indexOf('#');
    if (hashIndex == -1) return FAQ_URL;
    return `${FAQ_URL}${path.slice(hashIndex)}`;
}
function navigateToPageOnMobileAppViaReload(url) {
    const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
    const parsedUrl = new URL(fullUrl);
    const relativeUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    const mobileUrl = relativeUrl.includes('/mobile') ? relativeUrl : `/mobile${relativeUrl}`;
    history.pushState({}, '', mobileUrl);
    setTimeout(()=>window.location.reload(), 100);
}
export function navigateToPageOnMobileAppViaLink(url) {
    const link = document.createElement('a');
    link.href = decodeURIComponent(url);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
